import { useCallback, useEffect, useMemo, useState } from "react";
import { api, type User } from "./api";
import { forgetDemo, isDemoAccount, shownByDemo } from "./demo/flag";
import { applyProfile } from "./profile";
import { navigate, rememberNext, takeNext, useLocation } from "./router";
import { t } from "./i18n";
import { Device } from "./pages/Device";
import { Docs } from "./pages/Docs";
import { Group } from "./pages/Group";
import { Invite } from "./pages/Invite";
import { Login } from "./pages/Login";
import { Review } from "./pages/Review";
import { Session } from "./pages/Session";
import { Workbench } from "./pages/Workbench";
import { Shares } from "./pages/Shares";
import { Home } from "./pages/Home";
import { landing } from "./home";
import { Classes } from "./pages/Classes";
import { ClassPage } from "./pages/ClassDashboard";
import { ClassManage } from "./pages/ClassManage";
import { AssignmentPage } from "./pages/AssignmentPage";
import { JoinClass } from "./pages/JoinClass";
import { Exams } from "./pages/Exams";
import { Machines } from "./pages/Machines";
import { Agents } from "./pages/Agents";
import { AgentPage } from "./pages/AgentPage";
import { Admin } from "./pages/Admin";
import { Bank } from "./pages/Bank";
import { Billing } from "./pages/Billing";
import { Templates } from "./pages/Templates";
import { TemplatePage } from "./pages/TemplatePage";
import { Files } from "./pages/Files";
import { FilePage } from "./pages/FilePage";
import { ExamPage } from "./pages/ExamPage";
import { ExamAssignment } from "./pages/ExamAssignment";
import { ClassPreview } from "./pages/ClassPreview";
import { PageLoading, PageNotice } from "./components/Page";
import { PageBoundary } from "./components/PageBoundary";
import { Shell } from "./components/Shell";
import { EditionApp, wearsEdition } from "./editions";

/** An error is the relay's, tried again by asking it again, or the demo's that could not start, by reloading the page. */
type Me = { state: "loading" } | { state: "out" } | { state: "in"; user: User } | { state: "error"; message: string; reload?: boolean };

/**
 * The app. `keepsNext` false: the page a signed-out person asked for is
 * neither kept for their sign-in nor taken on it, as the class demo's, whose
 * sign-ins are its personas', leaves it to the real one (byj-1wv.4.19).
 * `onDemo`: a demo account signed in is handed to it, and the app draws no
 * page of the relay's, takes no page kept for a sign-in and goes nowhere
 * (byj-1wv.84.1); the demo that could not start is said as an error, with
 * Reload, since a module that failed to load fails again until the page is
 * loaded anew. Signed out, or signed in as no demo account, whom a demo
 * showed is forgotten, so the next opens anew.
 */
export function App({ keepsNext = true, onDemo }: { keepsNext?: boolean; onDemo?: (user: User) => void | Promise<unknown> } = {}) {
  const loc = useLocation();
  const [me, setMe] = useState<Me>({ state: "loading" });

  const load = useCallback(() => {
    setMe({ state: "loading" });
    api.me().then(
      (user) => {
        if (user) applyProfile(user);
        setMe(user ? { state: "in", user } : { state: "out" });
      },
      (err: unknown) => setMe({ state: "error", message: err instanceof Error ? err.message : String(err) }),
    );
  }, []);
  useEffect(load, [load]);

  const path = loc.path.replace(/\/+$/, "") || "/";
  // the guide and the plans on sale are for everyone, signed in or not
  const needsLogin = path !== "/login" && path !== "/docs" && path !== "/pricing";
  const demo = me.state === "in" && !!onDemo && isDemoAccount(me.user) ? me.user : null;
  // a page the demo showed in this tab, reached once it is left (Back after Leave): one of the
  // demo's things, which the real app neither keeps for after a sign-in nor asks its relay for
  // (byj-1wv.4.20); the app inside the demo, which keeps nothing, is the demo's and draws it
  // read from the tab's storage when the page changes, not on every render
  const demoPage = useMemo(() => keepsNext && !demo && shownByDemo(loc.path), [keepsNext, demo, loc.path]);

  useEffect(() => {
    if (!onDemo) return;
    if (me.state === "out" || (me.state === "in" && !demo)) forgetDemo();
    if (!demo) return;
    Promise.resolve(onDemo(demo)).catch((err: unknown) => setMe({ state: "error", message: err instanceof Error ? err.message : String(err), reload: true }));
  }, [demo, onDemo, me.state]);

  // Signed out on a page that needs a user: sign in first, then come back.
  useEffect(() => {
    if (me.state === "out" && needsLogin) {
      if (keepsNext && !demoPage) rememberNext(loc.path + window.location.search);
      navigate("/login", true);
    }
    if (me.state === "in" && !demo) {
      // the page a signed-out person asked for, once signed in, wherever the
      // sign-in landed: the login link's callback lands on /home, and that
      // page was kept and never gone back to (byj-1wv.10.1); else the home
      // page, a third party's people their own (byj-1wv.137)
      const next = keepsNext ? takeNext() : null;
      if (next && next !== loc.path + window.location.search) navigate(next, true);
      else if (path === "/" || path === "/login" || demoPage || (path === "/home" && me.user.integration)) navigate(landing(me.user), true);
    }
  }, [me.state, needsLogin, path, loc.path, keepsNext, demo, demoPage]);

  const logout = async () => {
    let exit: string | undefined;
    try {
      exit = (await api.logout())?.exit_url;
    } finally {
      // one signed in by a third party's link leaves to its system (RFC-062 D7)
      if (exit) window.location.assign(exit);
      else {
        setMe({ state: "out" });
        navigate("/login", true);
      }
    }
  };

  // a demo page waits only for what the effect sends on, signed out or in: a relay that could not
  // say who is signed in is said on it as on any page, with Retry
  if (me.state === "loading" || demo || (demoPage && me.state !== "error")) return <PageLoading />;
  if (me.state === "error") {
    return (
      <PageNotice error={me.message}>
        {me.reload ? <button onClick={() => window.location.reload()}>{t("common:reload")}</button> : <button onClick={load}>{t("common:retry")}</button>}
      </PageNotice>
    );
  }
  if (me.state === "out") {
    if (path === "/docs") {
      return (
        <Shell path={path} user={null}>
          <Docs signedIn={false} />
        </Shell>
      );
    }
    if (path === "/pricing") {
      return (
        <Shell path={path} user={null}>
          <Billing signedIn={false} query={loc.query} />
        </Shell>
      );
    }
    return path === "/login" ? <Login /> : null;
  }

  const user = me.user;
  // a third party's candidate or operator wears the profile's edition: its pages, not Share's (RFC-062)
  if (wearsEdition(user)) return <EditionApp name={user.ui!} user={user} path={path} query={loc.query} onSignOut={() => void logout()} />;
  if (path === "/" || path === "/login") return null;
  return (
    <Shell path={path} user={user} onLogout={() => void logout()}>
      {/* a page that throws is that page's, not the app's: the shell stays */}
      <PageBoundary resetKey={loc.path}>{page(path, user, loc.query)}</PageBoundary>
    </Shell>
  );
}

function page(path: string, user: User, query: URLSearchParams) {
  // where a sign-in lands: what Share does, and mine of it (byj-1wv.137)
  if (path === "/home") return <Home user={user} />;
  if (path === "/shares") return <Shares />;
  // a share's page fills the pane under the top bar, as a recording's does
  const s = /^\/s\/([^/]+)$/.exec(path);
  if (s) return <Session key={s[1]} id={decodeURIComponent(s[1]!)} me={user} />;
  // a machine's workbench (RFC-057): its files, an editor and its agent
  const wbm = /^\/s\/([^/]+)\/workbench$/.exec(path);
  if (wbm) return <Workbench key={wbm[1]} id={decodeURIComponent(wbm[1]!)} me={user} />;
  if (path === "/device") return <Device initialCode={query.get("code") ?? ""} machine={query.get("machine") === "1"} />;
  // a third party's people make no class or exam of their own here (byj-1wv.117.10)
  if (path === "/classes") return <Classes own={!user.integration} />;
  if (path === "/exams") return <Exams own={!user.integration} />;
  if (path === "/machines") return <Machines />;
  // the agent builder (RFC-060): my agents, and one in its steps
  if (path === "/agents") return <Agents />;
  const ag = /^\/agents\/([^/]+)$/.exec(path);
  if (ag) return <AgentPage key={ag[1]} name={decodeURIComponent(ag[1]!)} />;
  if (path === "/admin") return user.admin ? <Admin adminUntil={user.admin_until} /> : <PageNotice message={t("admin:adminNotAdmin")} />;
  // the item bank (RFC-056), for a person let into it
  if (path === "/bank" || path.startsWith("/bank/")) return <Bank path={path} me={user} query={query} />;
  // my plan and its payments, and the plans on sale (RFC-065)
  if (path === "/billing" || path === "/pricing") return <Billing signedIn query={query} />;
  if (path === "/templates") return <Templates />;
  if (path === "/files") return <Files />;
  if (path === "/docs") return <Docs signedIn />;
  if (path === "/c/join") {
    const code = query.get("code") ?? "";
    return <JoinClass key={code} initialCode={code} />;
  }
  let m = /^\/r\/([^/]+)$/.exec(path);
  if (m) return <Review key={m[1]} id={decodeURIComponent(m[1]!)} me={user} thread={query.get("thread")} />;
  m = /^\/g\/([^/]+)$/.exec(path);
  if (m) return <Group key={m[1]} id={decodeURIComponent(m[1]!)} />;
  m = /^\/i\/([^/]+)$/.exec(path);
  if (m) return <Invite key={m[1]} token={decodeURIComponent(m[1]!)} />;
  // an exam, and an exam assigned to a class (RFC-048)
  m = /^\/e\/([^/]+)$/.exec(path);
  if (m) return <ExamPage key={m[1]} id={decodeURIComponent(m[1]!)} />;
  m = /^\/ea\/([^/]+)$/.exec(path);
  if (m) return <ExamAssignment key={m[1]} id={decodeURIComponent(m[1]!)} cli={user.integration !== "candidate"} />;
  // an app template (RFC-052)
  m = /^\/t\/([^/]+)$/.exec(path);
  if (m) return <TemplatePage key={m[1]} id={decodeURIComponent(m[1]!)} />;
  // a file (RFC-054)
  m = /^\/f\/([^/]+)$/.exec(path);
  if (m) return <FilePage key={m[1]} id={decodeURIComponent(m[1]!)} me={user} thread={query.get("thread")} />;
  m = /^\/c\/([^/]+)\/manage$/.exec(path);
  if (m) return <ClassManage key={m[1]} id={decodeURIComponent(m[1]!)} />;
  m = /^\/c\/([^/]+)\/preview$/.exec(path);
  if (m) return <ClassPreview key={m[1]} id={decodeURIComponent(m[1]!)} />;
  m = /^\/c\/([^/]+)\/a\/([^/]+)$/.exec(path);
  if (m) return <AssignmentPage key={`${m[1]}/${m[2]}`} classId={decodeURIComponent(m[1]!)} assignmentKey={decodeURIComponent(m[2]!)} />;
  m = /^\/c\/([^/]+)$/.exec(path);
  if (m) return <ClassPage key={m[1]} id={decodeURIComponent(m[1]!)} me={user} />;
  return (
    <PageNotice message={t("common:notFound")}>
      <a href="/shares" onClick={(e) => (e.preventDefault(), navigate("/shares"))}>
        {t("common:backToShares")}
      </a>
    </PageNotice>
  );
}
